/* ============================================================================
   Curated Pin-on-Arrival
   ----------------------------------------------------------------------------
   Pins an ad-clicked product to slot #1 of the collection grid when the page
   is opened with a ?first= param (Meta catalog/static ad landing). When the
   product is already in the server-rendered grid it is REORDERED to the front
   (no fetch, no layout rebuild); otherwise a single card is fetched and
   prepended. The rest of the server-rendered grid is left untouched.

   Ship-dormant: with no ?first= param the script returns before touching the
   DOM, so organic collection browsing is completely unaffected.
============================================================================ */

(() => {
  const qp = new URLSearchParams(location.search);

  // ---- 0. Gate: do nothing unless this is a pinned ad landing ----------------
  const rawFirstParam = qp.get('first');
  if (!rawFirstParam) return;

  // ---- 1. Parse the pinned handle + variant ----------------------------------
  // ?first= accepts either a bare product handle OR a Facebook-encoded product
  // URL (catalog deep-link override). Decode once, then branch on shape.
  let rawFirst = '';
  try {
    rawFirst = decodeURIComponent(rawFirstParam).trim();
  } catch (_) {
    rawFirst = rawFirstParam.trim();
  }

  let firstHandle = '';
  let variantFromURL = '';

  if (rawFirst.startsWith('http')) {
    // Facebook deep-link form: https://shop.com/products/<handle>?variant=123&utm_...
    try {
      const u = new URL(rawFirst);
      firstHandle = handleFromPath(u.pathname);
      variantFromURL = u.searchParams.get('variant') || '';
    } catch (_) { /* fall through to empty handle */ }
  } else {
    // Plain form: a handle, or a relative /products/<handle> path.
    firstHandle = rawFirst.includes('/') ? handleFromPath(rawFirst) : rawFirst;
  }
  if (!firstHandle) return;

  // Variant id: digits only. Prefer explicit ?variant=, else the one embedded
  // in the Facebook URL. ?limit= is intentionally parse-tolerant but ignored —
  // we keep every server-rendered card and only add the pinned one.
  const variantCandidate = qp.get('variant') || variantFromURL || '';
  const variantID = /^\d+$/.test(variantCandidate) ? variantCandidate : '';

  // ---- 2. Locate the grid ----------------------------------------------------
  const grid = document.querySelector('[data-curated-grid]') || document.querySelector('.doc-grid');
  if (!grid) return;

  // ---- 3. Theme-editor preview passthrough -----------------------------------
  // In the theme editor the storefront fetch must carry preview params + cookies,
  // otherwise the card fetch 404s or renders the published theme.
  const previewExtras = ['preview_theme_id', 'preview_theme_token', 'pb']
    .filter((k) => qp.has(k))
    .map((k) => `${k}=${encodeURIComponent(qp.get(k) || '')}`)
    .join('&');
  const withPreview = (url) =>
    previewExtras ? `${url}${url.includes('?') ? '&' : '?'}${previewExtras}` : url;
  // Always send same-origin cookies: a theme preview is held in a session cookie
  // (the preview_theme_id param gets dropped on the redirect to the primary
  // domain), so the card fetch must carry cookies to render the same theme the
  // shopper is viewing. Harmless on the published storefront.
  const fetchCreds = 'same-origin';

  // ---- 4. Run ----------------------------------------------------------------
  pin().catch(() => { /* never break the page over a pin failure */ });

  async function pin() {
    const existing = findCardByHandle(grid, firstHandle);

    if (existing) {
      // Already in the grid → move to the absolute front. No fetch, no new node.
      grid.insertBefore(existing, grid.firstElementChild);
      finish(existing, 'reorder');
      return;
    }

    // Not on this page → drop a placeholder into slot #1 immediately. This gives
    // instant feedback and reserves the slot's height, so swapping in the real
    // card causes no further layout shift.
    const skeleton = buildSkeleton(grid.dataset.cardRatio);
    grid.insertBefore(skeleton, grid.firstElementChild);

    let card = null;
    try {
      card = await fetchCard(firstHandle, variantID);
    } catch (_) { /* network failure → fall through and clean up the skeleton */ }
    if (!card) { skeleton.remove(); return; } // bad handle / draft / error → restore grid

    matchGridRatio(card, grid.dataset.cardRatio);
    // Swap the skeleton for the real card — unless the grid was re-rendered out
    // from under us during the fetch, in which case just prepend.
    if (skeleton.parentNode === grid) grid.replaceChild(card, skeleton);
    else grid.insertBefore(card, grid.firstElementChild);
    finish(card, 'prepend');
  }

  function finish(cardEl, mode) {
    dedupe(grid, firstHandle);            // ensure a single instance
    // The pinned card represents one ad-chosen variant, so the colour-swatch row is
    // noise — remove it (covers the reorder path; the prepend template omits it).
    const dots = cardEl.querySelector('.bnc-color-dots');
    if (dots) dots.remove();
    decorateLink(cardEl, variantID);      // ?variant= + UTM/fbclid passthrough
    emit('curated_pin_view', { handle: firstHandle, variant: variantID || null, mode });
    wireClick(cardEl, { handle: firstHandle, variant: variantID || null });
  }

  // Shimmer placeholder shaped like a real card (reuses the grid's image-ratio
  // class so it reserves the same height).
  function buildSkeleton(ratio) {
    if (!buildSkeleton._css) {
      buildSkeleton._css = true;
      const s = document.createElement('style');
      s.textContent =
        '.cpin-skel{pointer-events:none}' +
        '.cpin-skel .bnc-pcard-img{position:relative;overflow:hidden;background:#ededed}' +
        '.cpin-skel .bnc-pcard-img::after{content:"";position:absolute;inset:0;transform:translateX(-100%);' +
        'background:linear-gradient(90deg,transparent,rgba(255,255,255,.65),transparent);animation:cpin-sh 1.1s infinite}' +
        '@keyframes cpin-sh{100%{transform:translateX(100%)}}' +
        '@media(prefers-reduced-motion:reduce){.cpin-skel .bnc-pcard-img::after{animation:none}}';
      document.head.appendChild(s);
    }
    const art = document.createElement('article');
    art.className = 'bnc-pcard cpin-skel';
    art.setAttribute('aria-hidden', 'true');
    const img = document.createElement('div');
    img.className = 'bnc-pcard-img bnc-pcard-img--' + (ratio || 'square');
    art.appendChild(img);
    return art;
  }

  // ---- helpers ---------------------------------------------------------------

  // Last path segment after a `products` segment, ignoring locale prefixes
  // (/en/products/<handle>) and trailing slashes. Returns '' when not a PDP path.
  function handleFromPath(pathname) {
    const segs = String(pathname).split('/').filter(Boolean);
    const i = segs.lastIndexOf('products');
    if (i !== -1 && segs[i + 1]) return segs[i + 1];
    return segs.length ? segs[segs.length - 1] : '';
  }

  function findCardByHandle(root, handle) {
    const cards = root.querySelectorAll('article.bnc-pcard');
    for (const card of cards) {
      const link = card.querySelector('a.bnc-pcard-link[href]');
      if (link && handleFromPath(new URL(link.href, location.origin).pathname) === handle) {
        return card;
      }
    }
    return null;
  }

  async function fetchCard(handle, variant) {
    const cacheKey = `cpin:card:${handle}:${variant || 'def'}`;

    // Session cache: within-session repeats / back-forward are instant (no network).
    let html = null;
    try { html = sessionStorage.getItem(cacheKey); } catch (_) { /* private mode */ }
    if (html) return elementFromHtml(html, handle);

    // Prefer the prefetch the layout kicked off in <head> during page parse — it
    // has been in flight in parallel with the render, so it's usually already done.
    const pf = window.__cpinPrefetch;
    if (pf && pf.handle === handle && (pf.variant || '') === (variant || '')) {
      html = await pf.promise;
    }
    if (html == null) {
      const url = withPreview(
        `/products/${encodeURIComponent(handle)}?view=card${variant ? `&variant=${variant}` : ''}`
      );
      // 'default' honors Shopify's Cache-Control so a later image/markup change isn't
      // pinned forever; the variant-specific URL still dedupes across variants.
      const res = await fetch(url, { credentials: fetchCreds, cache: 'default' });
      if (!res.ok) return null;
      html = await res.text();
    }
    html = (html || '').trim();
    if (!html) return null;
    // The curated-pin view must return a BARE card. If the theme lacks the
    // product.card template, Shopify falls back to the FULL product page (which
    // itself contains related-product .bnc-pcard cards) — reject that outright so
    // we never prepend a stray card.
    if (/<!doctype/i.test(html) || /<html[\s>]/i.test(html)) return null;

    const card = elementFromHtml(html, handle);
    if (!card) return null;
    try { sessionStorage.setItem(cacheKey, html); } catch (_) { /* quota/private */ }
    return card;
  }

  // Parse a bare-card HTML string into a verified <article.bnc-pcard> element, or
  // null if it isn't the product we asked for.
  function elementFromHtml(html, handle) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const card = tpl.content.querySelector('article.bnc-pcard');
    if (!card) return null;
    const link = card.querySelector('a.bnc-pcard-link[href]');
    const got = link ? handleFromPath(new URL(link.href, location.origin).pathname) : null;
    if (got !== handle) return null;
    return card;
  }

  // The ?view=card template renders with schema defaults, so its image ratio may
  // not match the live grid. Swap the modifier class to whatever the grid uses.
  function matchGridRatio(card, ratio) {
    if (!ratio) return;
    const img = card.querySelector('.bnc-pcard-img');
    if (!img) return;
    img.classList.remove('bnc-pcard-img--square', 'bnc-pcard-img--portrait', 'bnc-pcard-img--adapt');
    img.classList.add(`bnc-pcard-img--${ratio}`);
  }

  function dedupe(root, handle) {
    const matches = [];
    root.querySelectorAll('article.bnc-pcard').forEach((card) => {
      const link = card.querySelector('a.bnc-pcard-link[href]');
      if (link && handleFromPath(new URL(link.href, location.origin).pathname) === handle) {
        matches.push(card);
      }
    });
    // Keep the first (the pinned one at the front), drop any later duplicates.
    matches.slice(1).forEach((card) => card.remove());
  }

  // Carry the clicked variant + the landing page's attribution params onto the
  // pinned card's PDP link, so the click lands on the right variant and the
  // session stays attributable (pinned-landing vs straight-to-PDP comparison).
  function decorateLink(card, variant) {
    const link = card.querySelector('a.bnc-pcard-link[href]');
    if (!link) return;
    const u = new URL(link.href, location.origin);
    if (variant) u.searchParams.set('variant', variant);
    qp.forEach((val, key) => {
      if (key === 'fbclid' || key.startsWith('utm_')) u.searchParams.set(key, val);
    });
    link.setAttribute('href', u.pathname + u.search);
  }

  // Measurement: push to dataLayer when present (GTM/GA), and always dispatch a
  // DOM CustomEvent so analytics can be wired up later without a hard dependency.
  function emit(event, detail) {
    try {
      (window.dataLayer = window.dataLayer || []).push({ event, ...detail });
    } catch (_) { /* noop */ }
    try {
      window.dispatchEvent(new CustomEvent(`curated:${event.replace('curated_pin_', '')}`, { detail }));
    } catch (_) { /* noop */ }
  }

  function wireClick(card, detail) {
    const link = card.querySelector('a.bnc-pcard-link');
    if (!link) return;
    link.addEventListener('click', () => emit('curated_pin_click', detail), { once: true });
  }
})();
